import React from 'react';
import Icon from '../../../components/AppIcon';

const RuleStats = ({ rules = [], className = '' }) => {
  const calculateStats = () => {
    const totalRules = rules?.length;
    const activeRules = rules?.filter(rule => rule?.isActive)?.length;
    const inactiveRules = totalRules - activeRules;
    
    const totalMatches = rules?.reduce((sum, rule) => sum + (rule?.matchCount || 0), 0);
    const avgSuccessRate = rules?.length > 0 
      ? rules?.reduce((sum, rule) => sum + (rule?.successRate || 0), 0) / rules?.length 
      : 0;
    
    const patternTypes = rules?.reduce((acc, rule) => {
      acc[rule.patternType] = (acc?.[rule?.patternType] || 0) + 1;
      return acc;
    }, {});

    const confidenceDistribution = {
      high: rules?.filter(rule => rule?.confidenceThreshold >= 0.8)?.length,
      medium: rules?.filter(rule => rule?.confidenceThreshold >= 0.6 && rule?.confidenceThreshold < 0.8)?.length,
      low: rules?.filter(rule => rule?.confidenceThreshold < 0.6)?.length
    };

    return {
      totalRules,
      activeRules,
      inactiveRules,
      totalMatches,
      avgSuccessRate,
      patternTypes,
      confidenceDistribution
    };
  };

  const stats = calculateStats();

  const StatCard = ({ icon, label, value, color = 'text-foreground', description }) => (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center space-x-3 mb-2">
        <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center`}>
          <Icon name={icon} size={16} className={color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-2xl font-semibold ${color}`}>{value}</div>
          <div className="text-sm font-medium text-foreground">{label}</div>
        </div>
      </div>
      {description && (
        <div className="text-xs text-muted-foreground">{description}</div>
      )}
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Settings"
          label="Total Rules"
          value={stats?.totalRules}
          description="All matching rules in system"
        />
        
        <StatCard
          icon="CheckCircle"
          label="Active Rules"
          value={stats?.activeRules}
          color="text-success"
          description="Currently enabled for matching"
        />
        
        <StatCard
          icon="Target"
          label="Total Matches"
          value={stats?.totalMatches?.toLocaleString()}
          color="text-primary"
          description="Successful vendor matches"
        />
        
        <StatCard
          icon="TrendingUp"
          label="Avg Success Rate"
          value={`${Math.round(stats?.avgSuccessRate * 100)}%`}
          color="text-accent"
          description="Average rule performance"
        />
      </div>
      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pattern Type Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="PieChart" size={20} />
            <span>Pattern Types</span>
          </h3>
          
          <div className="space-y-3">
            {Object.entries(stats?.patternTypes)?.map(([type, count]) => {
              const percentage = stats?.totalRules > 0 ? (count / stats?.totalRules) * 100 : 0;
              const colors = {
                fuzzy: 'bg-primary',
                regex: 'bg-accent',
                exact: 'bg-secondary'
              };
              
              return (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground capitalize">{type} Matching</span>
                    <span className="text-muted-foreground">{count} rules ({Math.round(percentage)}%)</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${colors?.[type] || 'bg-muted-foreground'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Confidence Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="BarChart3" size={20} />
            <span>Confidence Levels</span>
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-success/10 rounded-md">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full" />
                <span className="text-sm font-medium text-foreground">High (≥80%)</span>
              </div>
              <span className="text-sm text-success font-semibold">
                {stats?.confidenceDistribution?.high} rules
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-warning/10 rounded-md">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-warning rounded-full" />
                <span className="text-sm font-medium text-foreground">Medium (60-79%)</span>
              </div>
              <span className="text-sm text-warning font-semibold">
                {stats?.confidenceDistribution?.medium} rules
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-error/10 rounded-md">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-error rounded-full" />
                <span className="text-sm font-medium text-foreground">Low (&lt;60%)</span>
              </div>
              <span className="text-sm text-error font-semibold">
                {stats?.confidenceDistribution?.low} rules
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Performance Insights */}
      {stats?.totalRules > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Lightbulb" size={20} />
            <span>Performance Insights</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Performing Rules */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Top Performing Rules</h4>
              <div className="space-y-2">
                {rules?.filter(rule => rule?.matchCount > 0)?.sort((a, b) => (b?.successRate || 0) - (a?.successRate || 0))?.slice(0, 3)?.map((rule, index) => (
                    <div key={rule?.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-primary">#{index + 1}</span>
                        <span className="text-sm text-foreground truncate">{rule?.name}</span>
                      </div>
                      <span className="text-xs text-success font-medium">
                        {Math.round((rule?.successRate || 0) * 100)}%
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Recommendations */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Recommendations</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                {stats?.inactiveRules > 0 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertCircle" size={14} className="text-warning mt-0.5" />
                    <span>You have {stats?.inactiveRules} inactive rules that could be enabled</span>
                  </div>
                )}
                
                {stats?.confidenceDistribution?.low > 0 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="TrendingDown" size={14} className="text-error mt-0.5" />
                    <span>{stats?.confidenceDistribution?.low} rules have low confidence thresholds</span>
                  </div>
                )}
                
                {stats?.avgSuccessRate < 0.7 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="Target" size={14} className="text-warning mt-0.5" />
                    <span>Consider reviewing patterns to improve overall success rate</span>
                  </div>
                )}
                
                {stats?.totalRules === 0 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="Plus" size={14} className="text-primary mt-0.5" />
                    <span>Create your first matching rule to automate vendor recognition</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleStats;